import React, { Suspense, useContext, useEffect, useState } from "react";
import { CancelTokenSource } from "axios";
import { useTranslation } from "react-i18next";
import { useSelector } from "react-redux";
import { Link } from "react-router-dom";
import { InfoDiagnostic2ReportApi } from "src/services/Api/Organization/types";
import { wrapPromise } from "go-core";
import Header from "go-app/components/Header";
import { LoadingContainer } from "go-core/components/Loading";
import { getSelectedSegmentForListConfig } from "go-list/list/services/segment-service";
import ReportData from "go-report/reports";
import {
	getLastMonthFilterBetween,
	getReportCustomFieldsResources,
	reportParamsConverter,
} from "go-report/reports/services/report-service";
import { ReportConfig } from "go-report/reports/services/types";
import { selectOrganization } from "go-security/services/organizations/selectors";
import { SegmentType } from "go-segment/components/types";
import { SegmentContext } from "go-segment/context";
import { apiOrganization } from "../../../../../../../../../services/Api/Organization/apiOrganization";
import { api } from "../../../../../../../../../services/Api/api";

interface ListState {
	resource?: Record<string, any>;
	currentData?: InfoDiagnostic2ReportApi;
}

const listName = "DIAGNOSTIC2_REPORT_STATES";
const resourceType = "DIAGNOSTIC2_REPORT_STATES";

const INITIAL_SELECTED_COLUMNS = [
	"open_amount",
	"open_value",
	"current_amount",
	"current_value",
	"diff_open_value",
	"diff_open_amount",
];

const Report = ({ resource, currentData }: ListState) => {
	const { t } = useTranslation();
	const segmentContext = useContext(SegmentContext);
	const organization = useSelector(selectOrganization);

	if (!resource) return null;

	const data = resource.read();

	let config = {
		reportConfigId: "DIAGNOSTIC2_REPORT_STATES",
		aggregatePrefix: "summary",
		type: "bill",
		selectedColumns: INITIAL_SELECTED_COLUMNS,
		showPercent: false,
		selectedGroups: ["PRODUCT"],
		groups: [
			{
				id: "PRODUCT",
				name: t("common.word.product"),
			},
			{
				id: "STOCK",
				name: t("common.word.stock"),
			},
			{
				id: "ERROR",
				name: "Error",
			},
		],
		groupsInfo: {
			PRODUCT: (groupByValue: Record<string, any>) => {
				return (
					<div>
						<Link
							to={`/${organization.id}/products/${groupByValue.id}/edit`}
							rel="noreferrer"
							target="_blank"
						>
							{groupByValue.name}
						</Link>
						{` | `}
						<Link
							to={`/${organization.id}/settings/diagnostic/layers?product_id=${groupByValue.id}`}
							// rel="noreferrer"
							// target="_blank"
						>
							DIAGNOSTYKA
						</Link>
					</div>
				);
			},
		},
		columns: [
			{
				id: "open_amount",
				name: "Otwarta ilość",
			},
			{
				id: "current_amount",
				name: "Aktualna ilość",
			},
			{
				id: "diff_open_amount",
				name: "Różnica ilości",
			},
			{
				id: "open_value",
				name: "Otwarta wartość",
			},
			{
				id: "current_value",
				name: "Aktualna wartość",
			},
			{
				id: "diff_open_value",
				name: "Różnica wartość",
			},
			{
				id: "archive_value",
				name: "Wartość otwartej archiwizacji",
			},
			{
				id: "poamount",
				name: "PO ilość",
			},
			{
				id: "document_poamount",
				name: "DOC PO ilość",
			},
			{
				id: "soamount",
				name: "SO ilość",
			},
			{
				id: "document_soamount",
				name: "DOC SO ilość",
			},
			{
				id: "toamount",
				name: "TO ilość",
			},
			{
				id: "document_toamount",
				name: "DOC TO ilość",
			},
		],
		filters: [
			{
				id: "stock_ids",
				name: t("common.word.stock"),
				type: "search_select",
				source: {
					request: (search: string, params: Record<string, any>, options?: Record<string, any>) =>
						api.organization().getStockSearchSelect(search, params, {
							cancelToken: options?.token,
						}),
				},
			},
			{
				id: "product_ids",
				name: t("common.word.product"),
				type: "search_select",
				source: {
					request: (search: string, params: Record<string, any>, options?: Record<string, any>) =>
						api.organization().getProductSearchSelect(search, params, {
							cancelToken: options?.token,
						}),
				},
			},
			{
				id: "open_amount",
				name: "Otwarta ilość",
				type: "number",
			},
			{
				id: "type",
				name: "typ błędu",
				type: "list",
				listOptions: {
					AMOUNT_ERROR: "AMOUNT_ERROR",
					VALUE_ERROR: "VALUE_ERROR",
					LAYER_ERROR: "LAYER_ERROR",
					LAYER_WARNING: "LAYER_WARNING",
					DOCUMENT_ERROR: "DOCUMENT_ERROR",
				},
			},
			{
				id: "date_range",
				name: t("common.word.date", { ns: "lib" }),
				type: "date",
				typeOptions: ["bt"],
			},
		],
		segments: [
			{
				id: "all",
				name: t("common.word.all", { ns: "lib" }),
				slug: "all",
				columns: INITIAL_SELECTED_COLUMNS,
				filters: [
					{
						filterId: "date_range",
						filterType: "date",
						value: getLastMonthFilterBetween({
							getDateFromLocalStorage: true,
							includeBoundaryDays: true,
						}).split("=")[1],
						condition: getLastMonthFilterBetween({
							getDateFromLocalStorage: true,
							includeBoundaryDays: true,
						}).split("=")[0],
					},
				],
				groups: ["PRODUCT"],
			},
		],
		selectedSegment: getSelectedSegmentForListConfig(data.segments, "all"),
		fetch: async (params: Record<string, any> = {}, sourceToken?: CancelTokenSource) => {
			delete params.chart_type;
			return Promise.all([
				apiOrganization.getDiagnostic2InfoReport(
					currentData,
					reportParamsConverter(params),
					sourceToken?.token
				),
			]);
		},
		saveSegment: async (segment: SegmentType) => {
			return segmentContext.save(listName, resourceType, segment);
		},
		exportConfig: {
			title: "Diagnostyka v2",
			filename: "Diagnostyka v2",
			organization: organization.name,
		},
	} as ReportConfig;

	data.fields = getReportCustomFieldsResources(data.fields, [], t);

	config = {
		...config,
		externalSegments: data.segments,
		fields: config.fields ? [...config.fields, ...data.fields] : data.fields,
		filterValues: data.filter_values,
	};

	return <ReportData config={config} />;
};

interface Props {
	data?: InfoDiagnostic2ReportApi;
}
const OrganizationSettingsDiagnostic2ReportStatesPage = (props: Props) => {
	const [resource, setResource] = useState<Record<string, any>>();
	const segmentContextValue = useContext(SegmentContext);
	const { t } = useTranslation();

	useEffect(() => {
		setResource(wrapPromise(segmentContextValue.get(listName, resourceType, [])));
	}, []);

	return (
		<>
			<Header title={t("modules.product_report.header.title")} />
			<Suspense fallback={<LoadingContainer />}>
				<Report resource={resource} currentData={props.data} />
			</Suspense>
		</>
	);
};

export default OrganizationSettingsDiagnostic2ReportStatesPage;
